import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { KeyRound, Check, X } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import Button from '../components/ui/Button'
import { PasswordField } from '../components/ui/Field'

/**
 * Forced password change for a platform administrator — a provisioning
 * password (from `platform:admin` or "add another administrator") must stop
 * working the first time it is used.
 */
export default function ChangePassword() {
  const { signOut, markPasswordChanged } = useAuth()
  const navigate = useNavigate()

  const [form, setForm] = useState({ current: '', next: '', confirm: '' })
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  const checks = useMemo(() => passwordChecks(form.next), [form.next])
  const allPassed = checks.every((check) => check.passed)
  const matches = form.next.length > 0 && form.next === form.confirm

  async function onSubmit(event) {
    event.preventDefault()

    setErrors({})

    if (!allPassed) {
      setErrors({ next: 'Please meet all the requirements below.' })
      return
    }

    if (!matches) {
      setErrors({ confirm: 'The two passwords do not match.' })
      return
    }

    setSubmitting(true)

    try {
      await api.post('/platform/change-password', {
        current_password: form.current,
        password: form.next,
        password_confirmation: form.confirm,
      })

      markPasswordChanged()
      navigate('/', { replace: true })
    } catch (err) {
      setErrors(
        err.fields?.current_password
          ? { current: err.fields.current_password[0] }
          : err.fields?.password
            ? { next: err.fields.password[0] }
            : { form: err.message },
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[var(--color-surface-sunken)] px-5 py-10">
      <div className="w-full max-w-md">
        <div className="rounded-lg border border-[var(--color-line)] bg-[var(--color-surface)] p-6">
          <div
            className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg text-white"
            style={{ backgroundColor: 'var(--color-accent)' }}
            aria-hidden="true"
          >
            <KeyRound size={19} />
          </div>

          <h1 className="font-display text-lg font-semibold text-[var(--color-ink-900)]">
            Choose your own password
          </h1>

          <p className="mt-1.5 text-sm leading-relaxed text-[var(--color-ink-500)]">
            Your account was set up with a temporary password. Choose one only you know before continuing.
          </p>

          <form onSubmit={onSubmit} className="mt-5 space-y-4" noValidate>
            {errors.form && (
              <div
                role="alert"
                className="rounded-md border px-3 py-2.5 text-sm"
                style={{
                  borderColor: 'var(--color-danger)',
                  backgroundColor: 'var(--color-danger-bg)',
                  color: 'var(--color-danger)',
                }}
              >
                {errors.form}
              </div>
            )}

            <PasswordField
              label="Temporary password"
              required
              value={form.current}
              onChange={(event) => setForm({ ...form, current: event.target.value })}
              autoComplete="current-password"
              error={errors.current}
            />

            <div>
              <PasswordField
                label="New password"
                required
                value={form.next}
                onChange={(event) => setForm({ ...form, next: event.target.value })}
                autoComplete="new-password"
                error={errors.next}
              />

              <ul className="mt-2.5 space-y-1" aria-label="Password requirements">
                {checks.map((check) => (
                  <li key={check.label} className="flex items-center gap-1.5 text-xs">
                    {check.passed ? (
                      <Check size={13} style={{ color: 'var(--color-success)' }} aria-hidden="true" />
                    ) : (
                      <X size={13} className="text-[var(--color-ink-400)]" aria-hidden="true" />
                    )}
                    <span style={{ color: check.passed ? 'var(--color-success)' : 'var(--color-ink-500)' }}>
                      {check.label}
                    </span>
                    <span className="sr-only">{check.passed ? '(met)' : '(not met)'}</span>
                  </li>
                ))}
              </ul>
            </div>

            <PasswordField
              label="Confirm new password"
              required
              value={form.confirm}
              onChange={(event) => setForm({ ...form, confirm: event.target.value })}
              autoComplete="new-password"
              error={errors.confirm ?? (form.confirm && !matches ? 'The two passwords do not match.' : undefined)}
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              loading={submitting}
              disabled={!allPassed || !matches || !form.current}
            >
              Change password
            </Button>
          </form>
        </div>

        <button
          type="button"
          onClick={signOut}
          className="mx-auto mt-4 block cursor-pointer text-sm text-[var(--color-ink-500)] hover:text-[var(--color-ink-900)] hover:underline"
        >
          Sign out instead
        </button>
      </div>
    </div>
  )
}

function passwordChecks(password) {
  return [
    { label: 'At least 10 characters', passed: password.length >= 10 },
    { label: 'Contains a letter and a number', passed: /[a-z]/i.test(password) && /\d/.test(password) },
  ]
}
